import {
  type ChatSession,
  getOneChatSession,
  useChatSessions,
} from "@/api/chat";
import { NewChatSessionDialog } from "@/components/chats/chat-config-dialog";
import ChatSessionDetail from "@/components/chats/chat-session-detail";
import { ChatSessionHeaderActions } from "@/components/chats/chat-session-header-actions";
import { SidePanelSwitcher } from "@/components/side-panel-switcher";
import { Button } from "@/components/ui/button";
import { ResizableSidePanel } from "@/components/ui/resizable-side-panel";
import { DATE_FORMAT } from "@/lib/utils";
import { useAppContext } from "@/providers/app-context";
import { ChatMessagesProvider } from "@/providers/chat-message-provider.component";
import { format } from "date-fns";
import { ChevronLeftIcon } from "lucide-react";
import React from "react";

const WIDTH_STORAGE_KEY = "forgeiq-project-chat-width";

export const ProjectChatPanel = () => {
  const {
    projectChatPanelProject: project,
    closeProjectChatPanel,
    currentViewedProject,
    openProjectChatPanel,
  } = useAppContext();

  const [selectedSessionId, setSelectedSessionId] = React.useState<string>();
  const [selectedSession, setSelectedSession] = React.useState<ChatSession>();

  // When the panel is open and the user navigates to a different project,
  // follow — swap the panel's scope to the project they're now viewing.
  React.useEffect(() => {
    if (!project || !currentViewedProject) return;
    if (currentViewedProject.id === project.id) return;
    openProjectChatPanel(currentViewedProject);
  }, [project, currentViewedProject, openProjectChatPanel]);

  // Reset the drilled-in session whenever the panel switches projects or closes.
  const projectId = project?.id;
  React.useEffect(() => {
    setSelectedSessionId(undefined);
    setSelectedSession(undefined);
  }, [projectId]);

  const sessions = useChatSessions({
    pageSize: 50,
    pageIndex: 1,
    ordering: { id: "created_at", desc: true },
    projectId,
    enabled: !!projectId,
  });

  React.useEffect(() => {
    if (!selectedSessionId) {
      setSelectedSession(undefined);
      return;
    }
    const fromList = sessions.data?.results?.find(
      (s) => s.id === selectedSessionId,
    );
    if (fromList) {
      setSelectedSession(fromList);
      return;
    }
    getOneChatSession(selectedSessionId)
      .then(setSelectedSession)
      .catch(() => setSelectedSessionId(undefined));
  }, [selectedSessionId, sessions.data?.results]);

  if (!project) return null;

  return (
    <ResizableSidePanel
      widthStorageKey={WIDTH_STORAGE_KEY}
      defaultWidth={560}
      onClose={closeProjectChatPanel}
      className="gap-y-3 pb-2"
    >
      <div className="flex items-center px-6 pt-3 pr-12">
        <SidePanelSwitcher mode="project-chats" />
      </div>
      {selectedSessionId && selectedSession ? (
        <>
          <div className="flex items-center gap-x-2 border-b px-6 pb-3 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedSessionId(undefined)}
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" />
              Back
            </Button>
            <span className="flex-1 truncate text-base font-semibold">
              {selectedSession.title || "Untitled chat"}
            </span>
            <ChatSessionHeaderActions
              chatSession={selectedSession}
              compact
              onDeleted={() => setSelectedSessionId(undefined)}
            />
          </div>
          <div className="flex min-h-0 flex-1">
            <ChatMessagesProvider>
              <ChatSessionDetail
                chatSession={selectedSession}
                embedded
              />
            </ChatMessagesProvider>
          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-y-3 px-6 pt-2">
          <div className="flex w-full items-center justify-between">
            <h2 className="flex-1 truncate text-base font-semibold">
              Chats · {project.name}
            </h2>
            <NewChatSessionDialog
              size="sm"
              projectId={project.id}
              onCreated={(session) => setSelectedSessionId(session.id)}
            />
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-y-1 overflow-y-auto">
            {sessions.isLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Loading…
              </div>
            )}
            {!sessions.isLoading &&
              (sessions.data?.results?.length ?? 0) === 0 && (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No chats for this project yet. Start one above.
                </div>
              )}
            {sessions.data?.results?.map((session) => (
              <button
                key={session.id}
                onClick={() => setSelectedSessionId(session.id)}
                className="flex flex-col items-start rounded-md border border-transparent px-3 py-2 text-left hover:border-gray-200 hover:bg-gray-50"
              >
                <span className="truncate text-sm font-medium">
                  {session.title || "Untitled chat"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {session.created_at
                    ? format(new Date(session.created_at), DATE_FORMAT)
                    : ""}
                  {session.users.length > 1
                    ? ` · ${session.users.length} members`
                    : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </ResizableSidePanel>
  );
};
